import type {
  AiSceneResult,
  AiSceneSegment,
  AiSceneTextItem,
} from '@/shared/apis/editor/post-ai-scene-generate';
import type { AnimationType } from '../../items/animation/animation-item-type';
import { generateRandomId } from '../../utils/generate-random-id';
import type { EditorState, TrackType } from '../types';
import type { TextItem } from '../../items/text/text-item-type';
import type { SolidItem } from '../../items/solid/solid-item-type';
import type { EditorStarterItem } from '../../items/item-type';
import { getZoneForItemType } from '../../utils/zone-utils';

const DEFAULT_FPS = 30;
const DEFAULT_FONT_FAMILY = 'Pretendard';

/**
 * AI 씬 결과를 EditorState에 반영합니다.
 * SceneResult의 각 세그먼트를 TextItem + SolidItem으로 변환하여 타임라인에 배치합니다.
 *
 * 같은 타입의 아이템은 하나의 트랙에 모읍니다:
 *   [Text 트랙]   ← 모든 TextItem이 하나의 트랙에 순차 배치 (최상위 레이어)
 *   [Solid 트랙]  ← 모든 SolidItem이 하나의 트랙에 순차 배치 (배경 레이어)
 *   [기존 트랙들]  ← 배열 뒤 = 타임라인 아래 = 최하위 레이어
 */
export const addAiScene = ({
  state,
  sceneResult,
}: {
  state: EditorState;
  sceneResult: AiSceneResult;
}): EditorState => {
  const fps = state.undoableState.fps || DEFAULT_FPS;
  const cw = state.undoableState.compositionWidth; // 예: 1080
  const ch = state.undoableState.compositionHeight; // 예: 1920

  const newItems: Record<string, EditorStarterItem> = {
    ...state.undoableState.items,
  };
  const newSelectedItems: string[] = [];

  // 타입별로 하나의 트랙에 아이템 ID를 모음
  const textItemIds: string[] = [];
  const solidItemIds: string[] = [];

  let frameCursor = 0; // 현재 타임라인 프레임 위치

  for (const segment of sceneResult.segments) {
    const segmentDurationFrames = Math.round(segment.durationSeconds * fps);
    // startSeconds가 지정되면 해당 위치, 아니면 이전 씬 끝에 이어서
    const segmentStartFrame =
      segment.startSeconds != null
        ? Math.round(segment.startSeconds * fps)
        : frameCursor;

    // 1. SolidItem (배경) 생성 → solid 트랙에 모음 (투명 배경이면 스킵)
    if (!segment.background.transparent) {
      const solidItem = createSolidItem({
        segment,
        startFrame: segmentStartFrame,
        durationFrames: segmentDurationFrames,
        compositionWidth: cw,
        compositionHeight: ch,
      });
      newItems[solidItem.id] = solidItem;
      solidItemIds.push(solidItem.id);
    }

    // 2. TextItem들 생성 → text 트랙에 모음
    for (const textItem of segment.textItems) {
      const delayFrames = Math.round(textItem.delaySeconds * fps);
      const textStartFrame = segmentStartFrame + delayFrames;
      const textDurationFrames = Math.max(
        segmentDurationFrames - delayFrames,
        1,
      );

      const editorTextItem = createTextItem({
        textItem,
        startFrame: textStartFrame,
        durationFrames: textDurationFrames,
        compositionWidth: cw,
        compositionHeight: ch,
      });
      newItems[editorTextItem.id] = editorTextItem;
      newSelectedItems.push(editorTextItem.id);
      textItemIds.push(editorTextItem.id);
    }

    frameCursor += segmentDurationFrames;
  }

  // 타입별 트랙 생성 (아이템이 있을 때만)
  const newTracks: TrackType[] = [];

  if (textItemIds.length > 0) {
    newTracks.push(createTrackWithItems(textItemIds));
  }
  if (solidItemIds.length > 0) {
    newTracks.push(createTrackWithItems(solidItemIds));
  }

  // 트랙 배치: 새 트랙(텍스트 → 배경) → 기존 트랙
  const mergedTracks = [...newTracks, ...state.undoableState.tracks];

  return {
    ...state,
    undoableState: {
      ...state.undoableState,
      tracks: mergedTracks,
      items: newItems,
    },
    selectedItems:
      newSelectedItems.length > 0 ? newSelectedItems : state.selectedItems,
  };
};

// ============================================================================
// Helper functions
// ============================================================================

function createSolidItem({
  segment,
  startFrame,
  durationFrames,
  compositionWidth,
  compositionHeight,
}: {
  segment: AiSceneSegment;
  startFrame: number;
  durationFrames: number;
  compositionWidth: number;
  compositionHeight: number;
}): SolidItem {
  return {
    id: generateRandomId(),
    type: 'solid',
    from: startFrame,
    durationInFrames: durationFrames,
    top: 0,
    left: 0,
    width: compositionWidth,
    height: compositionHeight,
    opacity: 1,
    color: segment.background.color,
    keepAspectRatio: false,
    fadeInDurationInSeconds: 0,
    fadeOutDurationInSeconds: 0,
    isDraggingInTimeline: false,
    borderRadius: 0,
    rotation: 0,
  };
}

function createTextItem({
  textItem,
  startFrame,
  durationFrames,
  compositionWidth,
  compositionHeight,
}: {
  textItem: AiSceneTextItem;
  startFrame: number;
  durationFrames: number;
  compositionWidth: number;
  compositionHeight: number;
}): TextItem {
  // 유효한 align 값인지 검증
  const validAligns = ['left', 'center', 'right'] as const;
  const align = validAligns.includes(
    textItem.align as (typeof validAligns)[number],
  )
    ? (textItem.align as 'left' | 'center' | 'right')
    : 'center';

  // AI가 반환한 상대 좌표 (0~1) → 픽셀 좌표로 변환
  const topPx = Math.round(textItem.top * compositionHeight);
  const leftPx = Math.round(textItem.left * compositionWidth);
  const widthPx = Math.round(textItem.width * compositionWidth);

  // AI가 반환한 height는 부정확하므로, 텍스트 내용 기반으로 높이 자동 계산
  const heightPx = estimateTextHeight({
    text: textItem.text,
    fontSize: textItem.fontSize,
    containerWidth: widthPx,
    lineHeight: 1.3,
  });

  return {
    id: generateRandomId(),
    type: 'text',
    from: startFrame,
    durationInFrames: durationFrames,
    top: topPx,
    left: leftPx,
    width: widthPx,
    height: heightPx,
    opacity: textItem.opacity ?? 1,
    text: textItem.text,
    color: textItem.color,
    align,
    fontFamily: textItem.fontFamily || DEFAULT_FONT_FAMILY,
    fontStyle: { variant: 'normal', weight: '700' },
    fontSize: textItem.fontSize,
    lineHeight: 1.3,
    letterSpacing: 0,
    resizeOnEdit: false,
    direction: 'ltr',
    strokeWidth: 0,
    strokeColor: '#000000',
    fadeInDurationInSeconds: textItem.fadeInSeconds,
    fadeOutDurationInSeconds: textItem.fadeOutSeconds,
    isDraggingInTimeline: false,
    rotation: 0,
    animations: textItem.animationType
      ? [
          {
            type: textItem.animationType as AnimationType,
            durationInSeconds: textItem.fadeInSeconds || 0.5,
          },
        ]
      : undefined,
  };
}

/**
 * 텍스트 내용과 폰트 크기를 기반으로 적절한 높이를 추정합니다.
 * AI가 반환하는 height는 실제 렌더링 크기를 모르므로 부정확합니다.
 */
function estimateTextHeight({
  text,
  fontSize,
  containerWidth,
  lineHeight,
}: {
  text: string;
  fontSize: number;
  containerWidth: number;
  lineHeight: number;
}): number {
  // 평균 글자 폭 추정 (한글은 fontSize에 가깝고, 영문은 ~0.55배)
  // 한글/CJK 비율에 따라 가중 평균
  const cjkCount = (text.match(/[\u3000-\u9fff\uac00-\ud7af]/g) || []).length;
  const totalCount = Math.max(1, text.length);
  const cjkRatio = cjkCount / totalCount;
  const avgCharWidth = fontSize * (cjkRatio * 0.9 + (1 - cjkRatio) * 0.55);

  // 줄바꿈 포함한 줄 수 계산
  const lines = text.split('\n');
  let totalLines = 0;
  for (const line of lines) {
    if (line.length === 0) {
      totalLines += 1;
    } else {
      const charsPerLine = Math.max(
        1,
        Math.floor(containerWidth / avgCharWidth),
      );
      totalLines += Math.ceil(line.length / charsPerLine);
    }
  }

  const textHeight = totalLines * fontSize * lineHeight;
  const padding = fontSize * 0.4;
  return Math.ceil(textHeight + padding);
}

function createTrackWithItems(itemIds: string[]): TrackType {
  return {
    id: generateRandomId(),
    items: itemIds,
    hidden: false,
    muted: false,
    locked: false,
    magnetic: false,
    zone: getZoneForItemType('text'),
  };
}
